/**
 * First-touch ad attribution, shared by the browser (which captures it) and the
 * server (which stamps it onto rows that outlive the visit).
 *
 * Why a cookie and not the URL: the free checker uses Post/Redirect/Get, so the
 * landing URL's `?gclid=…&utm_*` are gone by the time the visitor reaches the
 * email gate. Why not read PostHog's cookie: that blob is PostHog's private
 * format, and attribution we persist to our own tables should not depend on it.
 *
 * Deliberately NOT stored on `Scan`: a Scan row is a shared per-domain CACHE
 * entry that many visitors reuse, so per-visitor attribution written there would
 * be attributed to whoever happened to miss the cache first. It belongs on the
 * per-visitor row, which is `ScanReport`.
 *
 * This module must stay free of browser- and server-only imports so both sides
 * can use it.
 */

export const FIRST_TOUCH_COOKIE = 'cb_first_touch'
export const FIRST_TOUCH_MAX_AGE_SECONDS = 90 * 24 * 60 * 60

/** Keep the cookie small and stop junk/overlong values reaching the database. */
const MAX_VALUE_LENGTH = 200

/**
 * The attribution columns themselves. Named to match `ScanReport`, `User` and
 * `Order` exactly so a touch spreads straight into any Prisma `create`.
 */
export type FirstTouch = {
	gclid?: string
	utmSource?: string
	utmMedium?: string
	utmCampaign?: string
	utmTerm?: string
	utmContent?: string
	landingPage?: string
	referrer?: string
}

/**
 * What actually lives in the cookie: the columns plus WHEN the click happened.
 *
 * `capturedAt` is kept out of `FirstTouch` deliberately. A gclid is only valid
 * to Google for 90 days, so anything deciding whether a stored click may still
 * be uploaded needs the click's real age — and "when we wrote the row" is not
 * that. A visitor can click an ad on day 1 and create an account on day 89;
 * stamping the signup time would renew a nearly-dead click for another 90 days.
 * Only the cookie knows the true moment, so it has to carry it.
 *
 * It stays off `FirstTouch` because that type is the DB column shape, and only
 * `User` has a column to receive this. Use `firstTouchColumns()` before any
 * Prisma write.
 */
export type CapturedFirstTouch = FirstTouch & { capturedAt?: string }

/** Query param → FirstTouch key. Order is the field order in the cookie. */
const PARAM_TO_KEY = [
	['gclid', 'gclid'],
	['utm_source', 'utmSource'],
	['utm_medium', 'utmMedium'],
	['utm_campaign', 'utmCampaign'],
	['utm_term', 'utmTerm'],
	['utm_content', 'utmContent'],
] as const satisfies ReadonlyArray<readonly [string, keyof FirstTouch]>

function clean(value: string | null | undefined): string | undefined {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	if (!trimmed) return undefined
	return trimmed.slice(0, MAX_VALUE_LENGTH)
}

/**
 * Build a first touch from a landing URL. Returns null when the visit carries no
 * campaign markers at all — organic visits should not overwrite a real prior ad
 * click, and there is nothing worth storing.
 */
export function firstTouchFromUrl(
	url: URL,
	referrer?: string,
): CapturedFirstTouch | null {
	const touch: CapturedFirstTouch = {}
	for (const [param, key] of PARAM_TO_KEY) {
		const value = clean(url.searchParams.get(param))
		if (value) touch[key] = value
	}
	if (Object.keys(touch).length === 0) return null

	touch.landingPage = clean(url.pathname)
	const ref = clean(referrer)
	if (ref) touch.referrer = ref
	// The moment of the click, which is the only honest basis for expiring a
	// gclid later. Written here and never refreshed, matching first-touch-wins.
	touch.capturedAt = new Date().toISOString()
	return touch
}

export function serializeFirstTouch(touch: CapturedFirstTouch): string {
	return encodeURIComponent(JSON.stringify(touch))
}

/**
 * Just the Prisma columns. Every DB write goes through this so `capturedAt`,
 * which only `User` has a column for, can never leak into a `ScanReport` or
 * `Order` create.
 */
export function firstTouchColumns(
	touch: CapturedFirstTouch | null | undefined,
): FirstTouch {
	if (!touch) return {}
	const { capturedAt: _capturedAt, ...columns } = touch
	return columns
}

/**
 * When the click actually happened, or null when the cookie predates
 * `capturedAt` or carries an unusable value.
 *
 * Null means "unknown", and every caller must treat unknown as expired rather
 * than as fresh: guessing young on an unknown click is exactly how an
 * out-of-window gclid reaches a Google Ads upload.
 */
export function firstTouchCapturedAt(
	touch: CapturedFirstTouch | null | undefined,
): Date | null {
	if (!touch?.capturedAt) return null
	const at = new Date(touch.capturedAt)
	if (Number.isNaN(at.getTime())) return null
	// The cookie is client-writable. A future timestamp would extend the click's
	// apparent life indefinitely, and nothing older than the cookie's own max age
	// could have been written by us, so refuse both rather than trust them.
	const now = Date.now()
	if (at.getTime() > now) return null
	if (now - at.getTime() > FIRST_TOUCH_MAX_AGE_SECONDS * 1000) return null
	return at
}

export function parseFirstTouch(
	raw: string | null | undefined,
): CapturedFirstTouch | null {
	if (!raw) return null
	try {
		const parsed: unknown = JSON.parse(decodeURIComponent(raw))
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			return null
		}
		// Re-clean on read: the cookie is client-writable, so treat every value as
		// untrusted input rather than trusting what we wrote.
		const source = parsed as Record<string, unknown>
		const touch: CapturedFirstTouch = {}
		for (const key of [
			'gclid',
			'utmSource',
			'utmMedium',
			'utmCampaign',
			'utmTerm',
			'utmContent',
			'landingPage',
			'referrer',
			'capturedAt',
		] as const) {
			const value = clean(
				typeof source[key] === 'string' ? (source[key] as string) : undefined,
			)
			if (value) touch[key] = value
		}
		// A cookie carrying only `capturedAt` describes no click at all.
		return Object.keys(firstTouchColumns(touch)).length > 0 ? touch : null
	} catch {
		return null
	}
}

/** Read the first touch out of a request's Cookie header (server side). */
export function firstTouchFromCookieHeader(
	cookieHeader: string | null,
): CapturedFirstTouch | null {
	if (!cookieHeader) return null
	const prefix = `${FIRST_TOUCH_COOKIE}=`
	const match = cookieHeader
		.split(';')
		.map((part) => part.trim())
		.find((part) => part.startsWith(prefix))
	return match ? parseFirstTouch(match.slice(prefix.length)) : null
}
